'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { keccak256, encodeAbiParameters, pad, toHex } from 'viem';
import { clientPublicClient } from '@/lib/viem-client';

const POOL_MANAGER = process.env.NEXT_PUBLIC_V4_POOL_MANAGER_ADDRESS as `0x${string}` | undefined;

const HOOKS = '0x0000000000000000000000000000000000000000' as `0x${string}`;
const ZERO = '0x0000000000000000000000000000000000000000' as `0x${string}`;

const POOLS_SLOT = pad(toHex(6), { size: 32 });

const EXTSLOAD_ABI = [
  {
    name: 'extsload',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'slots', type: 'bytes32[]' }],
    outputs: [{ name: '', type: 'bytes32[]' }],
  },
] as const;

type PriceSource = 'exact' | 'alternative' | null;

interface PoolPriceResult {
  priceRatio: number | null;
  poolExists: boolean;
  isLoading: boolean;
  priceSource: PriceSource;
}

interface TokenLike {
  address: string;
  decimals: number;
}

const PRIMARY_FEE = 10000;
const PRIMARY_TICK_SPACING = 200;

// Same fee, varying tick spacings — covers pools created with different tick granularity
const TICK_SPACINGS = [200, 60, 10, 1];

function sortTokens(a: TokenLike, b: TokenLike): [TokenLike, TokenLike] {
  const addrA = a.address.toLowerCase();
  const addrB = b.address.toLowerCase();
  if (addrA === ZERO) return [a, b];
  if (addrB === ZERO) return [b, a];
  return addrA < addrB ? [a, b] : [b, a];
}

function computePoolId(
  currency0: `0x${string}`,
  currency1: `0x${string}`,
  fee: number,
  tickSpacing: number,
): `0x${string}` {
  return keccak256(
    encodeAbiParameters(
      [
        { type: 'address' },
        { type: 'address' },
        { type: 'uint24' },
        { type: 'int24' },
        { type: 'address' },
      ],
      [currency0, currency1, fee, tickSpacing, HOOKS],
    ),
  );
}

function computeStateSlot(poolId: `0x${string}`): `0x${string}` {
  return keccak256(
    encodeAbiParameters(
      [{ type: 'bytes32' }, { type: 'bytes32' }],
      [poolId, POOLS_SLOT],
    ),
  );
}

function sqrtPriceToRatio(sqrtPriceX96: bigint, decimals0: number, decimals1: number): number {
  const Q96 = 1n << 96n;
  const PRECISION = 1_000_000_000_000n;
  const numerator = sqrtPriceX96 * sqrtPriceX96 * PRECISION;
  const denominator = Q96 * Q96;
  const rawRatio = Number(numerator / denominator) / Number(PRECISION);
  const decimalAdj = 10 ** (decimals0 - decimals1);
  return rawRatio * decimalAdj;
}

const client = POOL_MANAGER ? clientPublicClient : null;

interface FetchResult {
  sqrtPriceX96: bigint;
  ratio: number;
  priceSource: PriceSource;
}

async function fetchPoolPrice(
  currency0: `0x${string}`,
  currency1: `0x${string}`,
  decimals0: number,
  decimals1: number,
): Promise<FetchResult> {
  if (!POOL_MANAGER || !client) throw new Error('Pool manager not configured');

  const slots = TICK_SPACINGS.map((ts) =>
    computeStateSlot(computePoolId(currency0, currency1, PRIMARY_FEE, ts))
  );

  const results = await client.readContract({
    address: POOL_MANAGER,
    abi: EXTSLOAD_ABI,
    functionName: 'extsload',
    args: [slots],
  });

  const MASK_160 = (1n << 160n) - 1n;

  for (let i = 0; i < results.length; i++) {
    const sqrtPriceX96 = BigInt(results[i]) & MASK_160;
    if (sqrtPriceX96 === 0n) continue;

    const isPrimary = TICK_SPACINGS[i] === PRIMARY_TICK_SPACING;

    return {
      sqrtPriceX96,
      ratio: sqrtPriceToRatio(sqrtPriceX96, decimals0, decimals1),
      priceSource: isPrimary ? 'exact' as const : 'alternative' as const,
    };
  }

  return { sqrtPriceX96: 0n, ratio: 0, priceSource: null };
}

export function useV4PoolPrice(
  tokenA: TokenLike | null,
  tokenB: TokenLike | null,
): PoolPriceResult & { isSwapped: boolean } {
  const sorted = useMemo(() => {
    if (!tokenA || !tokenB) return null;
    const [s0, s1] = sortTokens(tokenA, tokenB);
    const swapped = s0.address.toLowerCase() !== tokenA.address.toLowerCase();
    return {
      currency0: s0.address as `0x${string}`,
      currency1: s1.address as `0x${string}`,
      decimals0: s0.decimals,
      decimals1: s1.decimals,
      swapped,
    };
  }, [tokenA, tokenB]);

  const queryKey = sorted
    ? ['v4-pool-price', sorted.currency0.toLowerCase(), sorted.currency1.toLowerCase()]
    : ['v4-pool-price'];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      fetchPoolPrice(sorted!.currency0, sorted!.currency1, sorted!.decimals0, sorted!.decimals1),
    enabled: !!sorted && !!POOL_MANAGER,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const poolExists = !!data && data.sqrtPriceX96 > 0n;
  const priceSource: PriceSource = data?.priceSource ?? null;

  let priceRatio: number | null = null;
  if (sorted && poolExists && data!.ratio > 0) {
    priceRatio = sorted.swapped ? 1 / data!.ratio : data!.ratio;
  }

  return { priceRatio, poolExists, isLoading, isSwapped: sorted?.swapped ?? false, priceSource };
}
